import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Separator } from '@/components/ui/Separator';
import { 
  Building2, 
  User, 
  DollarSign, 
  Phone, 
  Mail, 
  MapPin,
  MessageSquare,
  Clock,
  GitBranch,
  Layers,
  Tag
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ClienteFornecedorWithDetails } from '@/types';

interface ClienteFornecedorModalProps {
  cliente: ClienteFornecedorWithDetails;
  isOpen: boolean;
  onClose: () => void;
}

const ClienteFornecedorModal: React.FC<ClienteFornecedorModalProps> = ({
  cliente,
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('geral');

  // Buscar detalhes completos do cliente
  const { data: clienteDetalhes, isLoading } = useQuery({
    queryKey: ['cliente-detalhes', cliente.id],
    queryFn: async () => {
      const response = await api.get(`/clientes-fornecedores/${cliente.id}`);
      return response.data.data;
    },
    enabled: isOpen,
  });

  const getStatusColor = (titulosVencidos: number, titulosEmAberto: number) => {
    if (titulosVencidos > 0) return 'destructive';
    if (titulosEmAberto > 0) return 'warning';
    return 'default';
  };

  const getStatusText = (titulosVencidos: number, titulosEmAberto: number) => {
    if (titulosVencidos > 0) return 'Vencido';
    if (titulosEmAberto > 0) return 'Em Aberto';
    return 'Em Dia';
  };

  const getFeedbackIcon = (tipo: string) => {
    switch (tipo) {
      case 'LIGACAO':
        return <Phone className="h-4 w-4" />;
      case 'EMAIL':
        return <Mail className="h-4 w-4" />;
      case 'WHATSAPP':
        return <MessageSquare className="h-4 w-4" />;
      case 'VISITA':
        return <MapPin className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getFeedbackColor = (tipo: string) => {
    switch (tipo) {
      case 'LIGACAO':
        return 'text-blue-600';
      case 'EMAIL':
        return 'text-green-600';
      case 'WHATSAPP':
        return 'text-green-500';
      case 'VISITA':
        return 'text-purple-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const titulos = (clienteDetalhes as any)?.titulos ?? [];

  const locNorm = (t: any) => String(t?.statusLocacao ?? '').trim().toUpperCase();
  const funilMatchesStatus = (t: any): boolean => {
    const nome = String(t?.kanban?.nome ?? '');
    const loc = locNorm(t);
    if (nome === 'Congelado') return loc === 'CONGELADA';
    if (nome === 'Cobranças') return loc === 'APROVADA';
    if (nome === 'Jurídico') return loc === 'CANCELADO' || loc === 'CANCELADA';
    if (nome === 'Multas') return true;
    return Boolean(t?.kanban || t?.kanbanEtapa);
  };

  const tituloRef =
    titulos.find((t: any) => (t.kanban || t.kanbanEtapa) && funilMatchesStatus(t)) ??
    titulos.find((t: any) => t.kanban || t.kanbanEtapa);

  const funilNome = tituloRef?.kanban?.nome ?? '—';
  const etapaNome = tituloRef?.kanbanEtapa?.nome ?? '—';
  const statusAtualLocacao = tituloRef
    ? locNorm(tituloRef) || '(sem status)'
    : '—';
  const kanbanDesatualizado =
    funilNome === 'Congelado' && statusAtualLocacao !== 'CONGELADA' && statusAtualLocacao !== '—';

  const kanbanIdOnde = tituloRef?.kanban?.id as string | undefined;
  const operadoresNoMesmoFunil =
    kanbanIdOnde != null
      ? [
          ...new Set(
            (titulos as any[])
              .filter((t: any) => t.kanban?.id === kanbanIdOnde && t.operador?.name)
              .map((t: any) => t.operador.name as string)
          ),
        ]
      : [];
  const operadorAoLadoEtapa =
    operadoresNoMesmoFunil.length > 0
      ? operadoresNoMesmoFunil.join(', ')
      : tituloRef?.operador?.name || cliente.operadorAtual?.name;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${cliente.nome} - Código: ${cliente.codigo}`}
      size="xl"
    >
      {/* Onde o cliente está: Funil, Etapa, Status da locação */}
      {!isLoading && titulos.length > 0 && (
        <Card className="mb-4 border-primary/30 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Onde está
              {kanbanDesatualizado ? (
                <Badge variant="destructive" className="text-[10px] font-normal">
                  Kanban desatualizado
                </Badge>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Funil</p>
                  <p className="font-medium">{funilNome}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Etapa</p>
                  <p className="font-medium">
                    {etapaNome}
                    {operadorAoLadoEtapa ? (
                      <span className="text-muted-foreground font-normal">
                        {' '}
                        · {operadorAoLadoEtapa}
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Status da locação</p>
                  <p className="font-medium">{statusAtualLocacao}</p>
                </div>
              </div>
            </div>
            {kanbanDesatualizado ? (
              <p className="mt-3 text-xs text-destructive">
                Este funil exige status CONGELADA. O título ainda aponta para Congelado com outro
                status — ao abrir o Kanban Congelado a fila é reconciliada automaticamente.
              </p>
            ) : null}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="geral">Informações Gerais</TabsTrigger>
          <TabsTrigger value="titulos">Títulos</TabsTrigger>
          <TabsTrigger value="historico">Histórico de Contatos</TabsTrigger>
        </TabsList>

        {/* Aba de Informações Gerais */}
        <TabsContent value="geral" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações Básicas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Nome:</span>
                  <span className="font-medium">{cliente.nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Código:</span>
                  <span className="font-medium">{cliente.codigo}</span>
                </div>
                {cliente.descricao && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Descrição:</span>
                    <span className="font-medium">{cliente.descricao}</span>
                  </div>
                )}
                {cliente.email && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="font-medium">{cliente.email}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge variant={getStatusColor(cliente.titulosVencidos, cliente.titulosEmAberto)}>
                    {getStatusText(cliente.titulosVencidos, cliente.titulosEmAberto)}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Informações da Empresa */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Empresa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Empresa:</span>
                  <span className="font-medium">{cliente.company.name}</span>
                </div>
                {cliente.operadorAtual && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Operador Atual:</span>
                    <span className="font-medium">{cliente.operadorAtual.name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Cadastrado em:</span>
                  <span className="font-medium">
                    {format(new Date(cliente.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resumo Financeiro */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Resumo Financeiro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{cliente.titulosEmAberto}</p>
                  <p className="text-sm text-muted-foreground">Títulos em Aberto</p>
                  <p className="text-xs font-medium">{formatCurrency(cliente.valorTotalEmAberto)}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{cliente.titulosVencidos}</p>
                  <p className="text-sm text-muted-foreground">Títulos Vencidos</p>
                  <p className="text-xs font-medium">{formatCurrency(cliente.valorTotalVencido)}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{cliente.totalTitulos}</p>
                  <p className="text-sm text-muted-foreground">Total de Títulos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {(clienteDetalhes as any)?.feedbacks?.length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Contatos Realizados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba de Títulos */}
        <TabsContent value="titulos" className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">Carregando títulos...</div>
          ) : (
            <div className="space-y-3">
              {(clienteDetalhes as any)?.titulos?.map((titulo: any) => (
                <Card key={titulo.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h4 className="font-semibold">{titulo.numero}</h4>
                          <Badge variant={titulo.status === 'VENCIDO' ? 'destructive' : 'default'}>
                            {titulo.status}
                          </Badge>
                          {titulo.statusLocacao && (
                            <Badge variant="outline" className="text-xs">
                              Locação: {titulo.statusLocacao}
                            </Badge>
                          )}
                          {titulo.kanban?.nome && (
                            <span className="text-xs text-muted-foreground">Funil: {titulo.kanban.nome}</span>
                          )}
                          {titulo.kanbanEtapa?.nome && (
                            <span className="text-xs text-muted-foreground">Etapa: {titulo.kanbanEtapa.nome}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Valor:</span>
                            <p className="font-medium">{formatCurrency(titulo.valor)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Saldo:</span>
                            <p className="font-medium">{formatCurrency(titulo.saldo)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Vencimento:</span>
                            <p className="font-medium">
                              {formatDate(titulo.vencimento)}
                            </p>
                          </div>
                          {titulo.operador && (
                            <div>
                              <span className="text-muted-foreground">Operador:</span>
                              <p className="font-medium">{titulo.operador.name}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Aba de Histórico de Contatos */}
        <TabsContent value="historico" className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">Carregando histórico...</div>
          ) : (
            <div className="space-y-6">
              {(clienteDetalhes as any)?.historicoContatos?.map((grupo: any) => (
                <Card key={grupo.operador.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      {grupo.operador.name}
                      <Badge variant="outline">{grupo.totalContatos} contatos</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {grupo.contatos.map((contato: any, index: number) => (
                        <div key={contato.id} className="border-l-2 border-blue-200 pl-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`${getFeedbackColor(contato.tipo)}`}>
                                {getFeedbackIcon(contato.tipo)}
                              </div>
                              <span className="font-medium capitalize">{contato.tipo.toLowerCase()}</span>
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(contato.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                              </span>
                            </div>
                            {contato.titulo && (
                              <Badge variant="outline" className="text-xs">
                                {contato.titulo.numero}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm mb-2">{contato.descricao}</p>
                          {contato.proximoContato && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              Próximo contato: {format(new Date(contato.proximoContato), 'dd/MM/yyyy', { locale: ptBR })}
                            </div>
                          )}
                          {contato.valorNegociado && (
                            <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                              <DollarSign className="h-3 w-3" />
                              Valor negociado: {formatCurrency(contato.valorNegociado)}
                            </div>
                          )}
                          {index < grupo.contatos.length - 1 && <Separator className="mt-3" />}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {(!clienteDetalhes?.historicoContatos || clienteDetalhes.historicoContatos.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum contato registrado ainda.</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Modal>
  );
};

export default ClienteFornecedorModal;
